import { INPUT_BUTTONS, type InputDrive, type InputMove, type Vec2 } from "@townsy/contracts";
import type { MotionState } from "@townsy/world";
import { NO_DRIVE, isMoving, type DriveIntent } from "./keyboard";
import type { Predictor } from "../net/prediction";

/** The part of the drive predictor the loop needs (net/drivePrediction.ts). */
export interface DriveStepper {
  /** One fixed step; returns the sequenced message to send, or null when nothing may be sent (held). */
  applyInput(intent: DriveIntent): InputDrive | null;
}

/**
 * Fixed 20 Hz local simulation tick (matches `movement.simulationHz`): sample the intent, step
 * the predictor, send the sequenced input while moving (plus one trailing idle input so the
 * server also stops), and hand the predicted state to the renderer.
 *
 * The step is a fixed timestep driven by wall time, not by timer punctuality: when the interval
 * callback arrives late (software rendering, a busy main thread) the missed steps are run in the
 * same callback, so the avatar walks at the manifest speed on every machine. Catch-up is bounded
 * to `maxStepsPerTick` (the server applies at most `movement.maxInputsPerTick` inputs per actor
 * per tick and drops the oldest beyond twice that), and time banked while a tab was throttled is
 * discarded, so a client can never move faster than the server simulation allows.
 *
 * Frame steps (ADR-0054 driving, ADR-0055 walking and venue vehicles): the session also calls `tick()`
 * at the start of every rendered frame. The accumulator makes any extra call harmless (it runs only the
 * steps that fell due: normally none, sometimes one), so a frame always finds the step it needs
 * computed even when the interval callback runs late; the interval keeps running for hidden tabs.
 *
 * Driving (ADR-0006): while `getDrive()` returns a stepper the same clock steps the car instead of
 * the walker and sends one `input.drive` per step, idle or not (the server steps a driven car only
 * with the inputs it receives, so coasting and braking need them too). Walking is never stepped
 * while driving.
 *
 * Running (ADR-0018): `getRun()` sets the `run` bit of each input; the shared step (and the server)
 * then covers RUN_SPEED_MULTIPLIER x the distance. Sitting / lying: while `isSeated()` nothing is
 * stepped or sent (the server holds the pose); `onSeated(moving)` reports the intent each tick so the
 * session can ask to stand up, and walking resumes from the server's stand-up spot.
 */
export interface MovementLoopDeps {
  hz: number;
  getIntent(): Vec2;
  getPredictor(): Predictor | null;
  send(input: InputMove): void;
  onTick(state: MotionState, moving: boolean): void;
  /** The car the local player drives (null while walking). */
  getDrive?(): DriveStepper | null;
  getDriveIntent?(): DriveIntent;
  sendDrive?(input: InputDrive): void;
  /** After the drive steps of one callback (the session draws the predicted car). */
  onDriveTick?(): void;
  /** Run instead of walk (Shift / the touch Run toggle). */
  getRun?(): boolean;
  /** The local actor sits or lies on a seat: walking is not predicted. */
  isSeated?(): boolean;
  /** Each tick while seated, with whether a movement intent is held. */
  onSeated?(moving: boolean): void;
  /** Steps run in one callback at most (default 3 = `movement.maxInputsPerTick`). */
  maxStepsPerTick?: number;
  /** Injectable clock (milliseconds) for tests. */
  now?: () => number;
}

export class MovementLoop {
  private timer: ReturnType<typeof setInterval> | null = null;
  private wasMoving = false;
  private lastTickAt: number | null = null;
  private accumulatorMs = 0;
  private readonly stepMs: number;
  private readonly maxSteps: number;
  private readonly now: () => number;

  constructor(private readonly deps: MovementLoopDeps) {
    this.stepMs = 1000 / deps.hz;
    this.maxSteps = Math.max(1, Math.floor(deps.maxStepsPerTick ?? 3));
    this.now = deps.now ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));
  }

  start(): void {
    if (this.timer) return;
    this.lastTickAt = null;
    this.accumulatorMs = 0;
    this.timer = setInterval(() => this.tick(), this.stepMs);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.wasMoving = false;
    this.lastTickAt = null;
    this.accumulatorMs = 0;
  }

  get running(): boolean {
    return this.timer !== null;
  }

  /** How many fixed steps this callback owes, given the wall time since the previous one (0 when none). */
  private dueSteps(): number {
    const t = this.now();
    if (this.lastTickAt === null) {
      this.lastTickAt = t;
      return 1;
    }
    this.accumulatorMs += Math.max(0, t - this.lastTickAt);
    this.lastTickAt = t;
    const owed = Math.floor(this.accumulatorMs / this.stepMs);
    if (owed > this.maxSteps) {
      // A stalled/throttled tab: run one bounded burst and discard the rest, never sprint later.
      this.accumulatorMs = 0;
      return this.maxSteps;
    }
    this.accumulatorMs -= owed * this.stepMs;
    return owed;
  }

  tick(): void {
    const drive = this.deps.getDrive?.() ?? null;
    const predictor = drive ? null : this.deps.getPredictor();
    if (!drive && !predictor) return;
    const steps = this.dueSteps();
    if (steps === 0) return; // callback arrived early: nothing is owed yet
    if (drive) {
      this.wasMoving = false;
      const intent = this.deps.getDriveIntent?.() ?? NO_DRIVE;
      for (let i = 0; i < steps; i++) {
        const msg = drive.applyInput(intent);
        if (msg) this.deps.sendDrive?.(msg);
      }
      this.deps.onDriveTick?.();
      return;
    }
    if (!predictor) return;
    const move = this.deps.getIntent();
    const moving = isMoving(move);
    if (this.deps.isSeated?.()) {
      // Seated: the server holds the pose; nothing is predicted or sent.
      this.wasMoving = false;
      this.deps.onSeated?.(moving);
      return;
    }
    const buttons = this.deps.getRun?.() ? INPUT_BUTTONS.run : 0;
    for (let i = 0; i < steps; i++) {
      const record = moving || this.wasMoving;
      const msg = predictor.applyInput({ moveX: move.x, moveZ: move.z, facing: predictor.state.facing, buttons }, { record });
      if (msg) this.deps.send(msg);
      this.wasMoving = moving;
      if (!moving) break; // idle: a single (possibly trailing) step is all the server needs
    }
    this.deps.onTick(predictor.state, moving);
  }
}
